/* Eloss — global runtime
 *
 * Spec: Part II §P.5 (vanilla ES2022, defer, custom elements with per-element
 * try/catch, no framework, no jQuery, no polyfills) and §P.6 (the public,
 * versioned DOM event contract).
 *
 * Progressive enhancement is mandatory for every purchase-critical path
 * (§P.5): nothing here is required for a page to render or for a form to
 * submit.
 */

window.Eloss = window.Eloss || {};

(() => {
  'use strict';

  /* ======================================================================
     Public DOM event contract — §P.6
     Versioned. Renaming one of these is a breaking change and requires a
     note in docs/architecture.md.
     ====================================================================== */

  const EVENTS = Object.freeze({
    cartAdded: 'eloss:cart:added',
    cartUpdated: 'eloss:cart:updated',
    cartError: 'eloss:cart:error',
    variantChanged: 'eloss:variant:changed',
    configuratorStep: 'eloss:configurator:step',
    searchPerformed: 'eloss:search:performed',
    filterApplied: 'eloss:filter:applied',
  });

  /**
   * Dispatch a contract event on `document`.
   * @param {string} name  one of EVENTS
   * @param {object} detail  documented payload for that event
   */
  function emit(name, detail = {}) {
    document.dispatchEvent(
      new CustomEvent(name, { detail, bubbles: true, cancelable: false })
    );
  }

  /* ======================================================================
     Custom element registration — §P.5
     Every custom element is defined behind try/catch so one broken element
     can never take down the rest of the page.
     ====================================================================== */

  /**
   * @param {string} name  must be an `eloss-*` tag (§P.2)
   * @param {CustomElementConstructor} ctor
   */
  function define(name, ctor) {
    if (!name.startsWith('eloss-')) {
      console.warn(`Eloss: custom element "${name}" must use the eloss- prefix.`);
      return;
    }
    if (customElements.get(name)) return;
    try {
      customElements.define(name, ctor);
    } catch (error) {
      console.error(`Eloss: failed to define <${name}>`, error);
    }
  }

  /* ======================================================================
     Small shared helpers
     ====================================================================== */

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Trailing debounce.
   * @param {Function} fn
   * @param {number} wait  ms — documented at each call site (§M, INP)
   */
  function debounce(fn, wait = 200) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  /* ======================================================================
     Scroll reveals — §B.7 / v1.0 §5.7
     IntersectionObserver at 12%, fired once, never above the fold, max 6
     staggered items per group. Disabled entirely under reduced motion.
     ====================================================================== */

  const REVEAL_THRESHOLD = 0.12;
  const REVEAL_MAX_STAGGER = 6;

  function initScrollReveals() {
    const targets = document.querySelectorAll('[data-eloss-reveal]');
    if (!targets.length) return;

    // Reduced motion, or no observer support: show everything immediately.
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.setAttribute('data-eloss-reveal', 'shown'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          const group = el.closest('[data-eloss-reveal-group]');
          let index = 0;

          if (group) {
            const items = Array.from(
              group.querySelectorAll('[data-eloss-reveal]')
            );
            index = Math.min(items.indexOf(el), REVEAL_MAX_STAGGER - 1);
          }

          el.style.setProperty('--reveal-index', String(Math.max(index, 0)));
          el.setAttribute('data-eloss-reveal', 'shown');
          observer.unobserve(el);
        });
      },
      { threshold: REVEAL_THRESHOLD }
    );

    targets.forEach((el) => {
      // Never reveal above-the-fold content — it costs LCP (v1.0 §5.7).
      if (el.getBoundingClientRect().top < window.innerHeight) {
        el.setAttribute('data-eloss-reveal', 'shown');
        return;
      }
      observer.observe(el);
    });
  }

  /* ======================================================================
     Boot
     ====================================================================== */

  function init() {
    document.documentElement.classList.remove('no-js');
    initScrollReveals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* Public surface. Kept deliberately small. */
  Object.assign(window.Eloss, {
    EVENTS,
    emit,
    define,
    debounce,
    prefersReducedMotion,
  });
})();
